import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CategoryProducts = () => {
  const { categorySlug } = useParams();
  const [category, setCategory] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const catResponse = await axios.get(`${API}/categories/${categorySlug}`);
        setCategory(catResponse.data);
        
        const prodResponse = await axios.get(`${API}/products?category_id=${catResponse.data.id}`);
        setProducts(prodResponse.data);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [categorySlug]);

  if (loading) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!category) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-outfit text-2xl text-white mb-4">Category not found</h1>
          <Link to="/shop" className="text-[#D4AF37] hover:underline">
            Back to Shop
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20" data-testid="category-products-page">
      {/* Header */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-[#141414] border-b border-white/10">
        <div className="max-w-7xl mx-auto">
          <Link 
            to="/shop" 
            className="inline-flex items-center text-gray-400 hover:text-[#D4AF37] mb-6 transition-colors"
            data-testid="back-to-shop"
          >
            <ArrowLeft size={18} className="mr-2" />
            Back to Shop
          </Link>
          <h1 className="font-outfit text-4xl sm:text-5xl text-white mb-4">
            <span className="gold-gradient">{category.name}</span>
          </h1>
          <p className="text-gray-400">
            Browse our selection of {category.name.toLowerCase()}
          </p>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {products.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-gray-400">No products found in this category.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {products.map((product) => (
                <Link
                  key={product.id}
                  to={`/product/${product.slug}`}
                  className="group bg-[#141414] border border-white/10 rounded-sm overflow-hidden product-card"
                  data-testid={`product-${product.slug}`}
                >
                  <div className="aspect-square overflow-hidden">
                    <img
                      src={product.image || "https://images.unsplash.com/photo-1633415565464-e1d69c3fd906?w=400"}
                      alt={product.name}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                  </div>
                  <div className="p-5">
                    <h3 className="font-outfit text-lg text-white group-hover:text-[#D4AF37] transition-colors mb-2">
                      {product.name}
                    </h3>
                    <span className="text-[#D4AF37] text-sm flex items-center">
                      View Details <ArrowRight size={14} className="ml-1 group-hover:translate-x-1 transition-transform" />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default CategoryProducts;
